import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { ArrowRight, Heart, Target, Sparkles, Users, Shield, Zap } from 'lucide-react';

const values = [
  {
    icon: Target,
    title: 'Results-Driven',
    description: 'We measure success by the outcomes we deliver. Every strategy, design decision, and line of code is focused on achieving measurable results for your business.'
  },
  {
    icon: Heart,
    title: 'Client-First',
    description: 'Your success is our success. We build lasting partnerships based on trust, transparency, and a genuine commitment to your goals.'
  },
  {
    icon: Sparkles,
    title: 'Innovation',
    description: 'We embrace new technologies and creative approaches to solve complex problems and deliver solutions that set you apart from the competition.'
  },
  {
    icon: Users,
    title: 'Collaboration',
    description: 'We work as an extension of your team, maintaining open communication and involving you in every important decision along the way.'
  },
  {
    icon: Shield,
    title: 'Integrity',
    description: 'We operate with honesty and transparency in everything we do. No hidden fees, no surprises—just straightforward, reliable service.'
  },
  {
    icon: Zap,
    title: 'Excellence',
    description: 'We hold ourselves to the highest standards. From strategy to execution, we strive for excellence in every aspect of our work.'
  }
];

const mission = {
  title: 'Our Mission',
  description: 'To empower businesses with innovative digital solutions that drive growth, build lasting connections with their audiences, and create measurable impact in the digital landscape.'
};

const vision = {
  title: 'Our Vision',
  description: 'To be the most trusted digital partner for businesses seeking to transform their online presence and achieve sustainable digital success.'
};

export default function ValuesPage() {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-muted/50 to-background">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <Badge variant="secondary" className="mb-6">
              <Heart className="h-4 w-4 mr-2" />
              Values & Mission
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-display font-bold text-foreground mb-6">
              What{' '}
              <span className="gradient-text">Drives Us</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Our values guide everything we do, from the way we work to the solutions we create.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding-sm bg-background">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="p-8 h-full gradient-primary text-primary-foreground border-none">
                <h2 className="text-2xl font-display font-bold mb-4">{mission.title}</h2>
                <p className="text-primary-foreground/90 leading-relaxed">{mission.description}</p>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="p-8 h-full bg-foreground text-background border-none">
                <h2 className="text-2xl font-display font-bold mb-4">{vision.title}</h2>
                <p className="text-background/90 leading-relaxed">{vision.description}</p>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Grid */}
      <section className="section-padding bg-muted/30">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-sm font-medium text-primary uppercase tracking-wider">Our Core Values</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mt-3">
              The Principles We{' '}
              <span className="gradient-text">Live By</span>
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="p-6 h-full border-border hover:border-primary/30 transition-all duration-300 card-hover">
                    <div className="w-14 h-14 rounded-xl gradient-primary flex items-center justify-center mb-4">
                      <Icon className="h-7 w-7 text-primary-foreground" />
                    </div>
                    <h3 className="text-xl font-display font-semibold text-foreground mb-3">
                      {value.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {value.description}
                    </p>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 gradient-primary">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-primary-foreground mb-6">
              Ready to Work With a Values-Driven Team?
            </h2>
            <p className="text-primary-foreground/80 mb-8">
              Let's create something meaningful together.
            </p>
            <Button variant="premium" size="lg" asChild className="bg-background text-foreground hover:bg-background/90">
              <Link to="/get-started">
                Get Started
                <ArrowRight className="h-5 w-5 ml-2" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
